import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ChatMedia } from "@/components/chat/ChatMedia";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Search, Loader2, RefreshCw, Star, Settings2, BarChart3, Trash2 } from "lucide-react";
import { usePager, PagerBar } from "@/components/admin/usePager";

const fmtDT = (iso) => { try { return new Date(iso).toLocaleString("fr-FR"); } catch { return ""; } };
const fmtDay = (d) => { try { return new Date(d + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "short", day: "numeric" }); } catch { return d; } };

// Dernière étape (super admin) : suppression DÉFINITIVE d'un message — base + fichiers, irréversible.
const PurgeMessage = ({ msg, onPurged }) => {
  const [arm, setArm] = useState(false);
  const [busy, setBusy] = useState(false);
  const purge = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/admin/chat/messages/${msg.id}/permanent`);
      toast.success(data.files_removed ? `Message et ${data.files_removed} fichier(s) supprimés définitivement` : "Message supprimé définitivement");
      onPurged?.(msg.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Suppression impossible"); }
    finally { setBusy(false); }
  };
  if (!arm) {
    return (
      <button onClick={() => setArm(true)} className="text-[11px] inline-flex items-center gap-1 rounded-full px-2 py-0.5 border hover:bg-red-50" style={{ color: "#7F1D1D", borderColor: "#FCA5A5" }} data-testid={`audit-msg-purge-${msg.id}`}>
        <Trash2 size={11} /> Supprimer définitivement
      </button>
    );
  }
  return (
    <div className="flex items-center gap-2 flex-wrap text-[11px]" data-testid={`audit-msg-purge-confirm-row-${msg.id}`}>
      <span className="font-semibold" style={{ color: "#7F1D1D" }}>Irréversible : {msg.media_url || msg.media_group?.length ? "le message et ses fichiers disparaîtront" : "le message disparaîtra"} pour tout le monde.</span>
      <Button size="sm" variant="ghost" className="rounded-full h-7 text-xs" onClick={() => setArm(false)} disabled={busy} data-testid={`audit-msg-purge-cancel-${msg.id}`}>Annuler</Button>
      <Button size="sm" className="rounded-full h-7 text-xs text-white" style={{ background: "#7F1D1D" }} onClick={purge} disabled={busy} data-testid={`audit-msg-purge-confirm-${msg.id}`}>{busy ? <Loader2 size={12} className="animate-spin" /> : <><Trash2 size={12} className="mr-1" /> Confirmer</>}</Button>
    </div>
  );
};

// Statistiques de la messagerie (GET /admin/chat/stats) : cartes + messages par jour (7 j).
const ChatStats = ({ stats, onRefresh, loading }) => {
  const perDay = stats?.per_day || [];
  const max = Math.max(1, ...perDay.map((d) => d.count));
  const cards = stats ? [
    ["messages_24h", "Messages · 24 h", stats.messages_24h],
    ["messages_7d", "Messages · 7 j", stats.messages_7d],
    ["conversations_active_7d", "Conversations actives · 7 j", stats.conversations_active_7d],
    ["voice_7d", "Vocaux · 7 j", stats.voice_7d],
    ["media_7d", "Photos / vidéos · 7 j", stats.media_7d],
    ["deleted_7d", "Supprimés · 7 j", stats.deleted_7d],
    ["masked_7d", "Mots masqués · 7 j", stats.masked_7d],
    ["requests_pending", "Demandes en attente", stats.requests_pending],
    ["reports_pending", "Signalements en attente", stats.reports_pending],
  ] : [];
  return (
    <div className="yalola-card p-4" data-testid="chat-stats-card">
      <div className="flex items-center gap-2 mb-3">
        <BarChart3 size={18} style={{ color: "var(--yalola-primary)" }} />
        <div className="font-bold flex-1">Statistiques de la messagerie</div>
        <button onClick={onRefresh} className="h-8 w-8 grid place-items-center rounded-full hover:bg-black/5" data-testid="chat-stats-refresh" aria-label="Actualiser">{loading ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}</button>
      </div>
      {!stats && <div className="text-sm" style={{ color: "var(--yalola-text-muted)" }}>Chargement…</div>}
      {stats && (
        <>
          <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-2">
            {cards.map(([k, label, v]) => (
              <div key={k} className="rounded-xl p-2.5" style={{ background: "var(--yalola-surface-alt)" }} data-testid={`chat-stat-${k}`}>
                <div className="text-lg font-bold leading-tight" style={{ color: k === "reports_pending" && v > 0 ? "#B91C1C" : k === "requests_pending" && v > 0 ? "#B45309" : "var(--yalola-text)" }}>{v ?? 0}</div>
                <div className="text-[10px] leading-tight" style={{ color: "var(--yalola-text-muted)" }}>{label}</div>
              </div>
            ))}
          </div>
          <div className="mt-3">
            <div className="text-[11px] font-semibold mb-1" style={{ color: "var(--yalola-text-muted)" }}>Messages par jour (7 derniers jours)</div>
            <div className="flex items-end gap-1.5 h-20" data-testid="chat-stats-per-day">
              {perDay.map((d) => (
                <div key={d.day} className="flex-1 flex flex-col items-center gap-1 min-w-0" title={`${d.day} : ${d.count}`} data-testid={`chat-stat-day-${d.day}`}>
                  <div className="text-[10px] font-semibold">{d.count}</div>
                  <div className="w-full rounded-t-md" style={{ height: `${Math.max(3, Math.round((d.count / max) * 52))}px`, background: "var(--yalola-primary)", opacity: d.count ? 1 : 0.25 }} />
                  <div className="text-[9px] truncate w-full text-center" style={{ color: "var(--yalola-text-muted)" }}>{fmtDay(d.day)}</div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Réglages Admin › Messages (GET/PATCH /admin/chat/settings) — super admin.
const ChatSettings = ({ initial, onSaved }) => {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (initial) setS({ ...initial, banned_words: (initial.banned_words || []).join(", ") }); }, [initial]);
  if (!s) return null;
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/chat/settings", {
        dm_privacy_default: s.dm_privacy_default,
        message_requests_enabled: !!s.message_requests_enabled,
        edit_window_min: Number(s.edit_window_min) || 15,
        voice_max_seconds: Number(s.voice_max_seconds) || 300,
        banned_words: s.banned_words,
      });
      toast.success("Réglages de la messagerie enregistrés");
      onSaved?.(data.chat);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };
  return (
    <details className="yalola-card p-4" data-testid="chat-settings-card">
      <summary className="cursor-pointer font-bold flex items-center gap-2 list-none" data-testid="chat-settings-toggle"><Settings2 size={18} style={{ color: "var(--yalola-primary)" }} /> Réglages de la messagerie</summary>
      <div className="mt-3 grid md:grid-cols-2 gap-4">
        <div className="space-y-1">
          <div className="text-xs font-semibold">Qui peut écrire à un membre (par défaut)</div>
          <Select value={s.dm_privacy_default} onValueChange={(v) => setS({ ...s, dm_privacy_default: v })}>
            <SelectTrigger className="h-9" data-testid="chat-set-dm-default"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="everyone">Tout le monde (inconnus → Demandes)</SelectItem>
              <SelectItem value="mutual">Abonnés mutuels seulement</SelectItem>
              <SelectItem value="nobody">Personne (sauf conversations existantes)</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>Chaque membre peut changer ce choix dans Réglages › Confidentialité.</p>
        </div>
        <div className="flex items-center justify-between gap-3 rounded-xl p-3" style={{ background: "var(--yalola-surface-alt)" }}>
          <div>
            <div className="text-xs font-semibold">Demandes de messages</div>
            <p className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>Les messages d'inconnus (le destinataire ne les suit pas) attendent dans « Demandes » sans notification.</p>
          </div>
          <Switch checked={!!s.message_requests_enabled} onCheckedChange={(v) => setS({ ...s, message_requests_enabled: v })} data-testid="chat-set-requests" />
        </div>
        <div className="space-y-1">
          <div className="text-xs font-semibold">Délai pour modifier son message (minutes)</div>
          <Input type="number" min={1} max={1440} value={s.edit_window_min} onChange={(e) => setS({ ...s, edit_window_min: e.target.value })} className="h-9" data-testid="chat-set-edit-window" />
          <p className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>La suppression « pour tout le monde » n'a aucune limite de temps.</p>
        </div>
        <div className="space-y-1">
          <div className="text-xs font-semibold">Durée max d'un message vocal (secondes)</div>
          <Input type="number" min={10} max={1800} value={s.voice_max_seconds} onChange={(e) => setS({ ...s, voice_max_seconds: e.target.value })} className="h-9" data-testid="chat-set-voice-max" />
        </div>
        <div className="md:col-span-2 space-y-1">
          <div className="text-xs font-semibold">Mots interdits (séparés par des virgules)</div>
          <Textarea rows={2} value={s.banned_words} onChange={(e) => setS({ ...s, banned_words: e.target.value })} placeholder="ex. insulte1, insulte2" className="text-sm" data-testid="chat-set-banned-words" />
          <p className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>Masqués par des astérisques dans le chat ; le texte original reste visible ici (badge « Mot masqué »).</p>
        </div>
      </div>
      <div className="mt-3 flex justify-end">
        <Button size="sm" className="pill-primary rounded-full" onClick={save} disabled={saving} data-testid="chat-settings-save">{saving ? <Loader2 size={14} className="animate-spin" /> : "Enregistrer"}</Button>
      </div>
    </details>
  );
};

const Badge = ({ children, bg, color, testId }) => (
  <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold" style={{ background: bg, color }} data-testid={testId}>{children}</span>
);

// Messages (audit) — super admin : conversations (recherche + filtre), fil avec NOMS et badges.
// Suppression DÉFINITIVE de toute la conversation (super admin) : messages + fichiers + conversation, irréversible.
const PurgeConversation = ({ conv, count, onPurged }) => {
  const [arm, setArm] = useState(false);
  const [busy, setBusy] = useState(false);
  const purge = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/admin/chat/conversations/${conv.id}/permanent`);
      toast.success(`Conversation supprimée définitivement (${data.messages_removed} message(s), ${data.files_removed} fichier(s))`);
      onPurged?.(conv.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Suppression impossible"); }
    finally { setBusy(false); }
  };
  if (!arm) {
    return (
      <button onClick={() => setArm(true)} className="text-[11px] inline-flex items-center gap-1 rounded-full px-2.5 py-1 border hover:bg-red-50" style={{ color: "#7F1D1D", borderColor: "#FCA5A5" }} data-testid={`audit-conv-purge-${conv.id}`}>
        <Trash2 size={11} /> Supprimer définitivement la conversation
      </button>
    );
  }
  return (
    <div className="rounded-xl p-2 flex items-center gap-2 flex-wrap text-[11px]" style={{ background: "#FEF2F2", border: "1px solid #FCA5A5" }} data-testid={`audit-conv-purge-confirm-row-${conv.id}`}>
      <span className="font-semibold" style={{ color: "#7F1D1D" }}>Irréversible : les {count} message(s) et leurs fichiers seront effacés, la conversation disparaîtra des deux boîtes.</span>
      <Button size="sm" variant="ghost" className="rounded-full h-7 text-xs" onClick={() => setArm(false)} disabled={busy} data-testid={`audit-conv-purge-cancel-${conv.id}`}>Annuler</Button>
      <Button size="sm" className="rounded-full h-7 text-xs text-white" style={{ background: "#7F1D1D" }} onClick={purge} disabled={busy} data-testid={`audit-conv-purge-confirm-${conv.id}`}>{busy ? <Loader2 size={12} className="animate-spin" /> : <><Trash2 size={12} className="mr-1" /> Tout supprimer</>}</Button>
    </div>
  );
};

export const ChatAuditPanel = () => {
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [convos, setConvos] = useState([]);
  const convoPager = usePager(convos);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);

  const loadStats = () => { setStatsLoading(true); api.get("/admin/chat/stats").then(({ data }) => setStats(data)).catch(() => {}).finally(() => setStatsLoading(false)); };
  useEffect(loadStats, []);
  useEffect(() => {
    const tmo = setTimeout(() => {
      api.get("/admin/chats", { params: { q: q.trim() || undefined, filter } }).then(({ data }) => setConvos(data.conversations || [])).catch(() => {});
    }, q ? 300 : 0);
    return () => clearTimeout(tmo);
  }, [q, filter]);

  const open = async (c) => {
    setSelected(c); setLoadingMsgs(true);
    try { const { data } = await api.get(`/chat/conversations/${c.id}/messages`, { params: { limit: 100 } }); setMsgs(data.messages || []); }
    catch { setMsgs([]); }
    finally { setLoadingMsgs(false); }
  };
  const names = Object.fromEntries((selected?.participant_details || []).map((p) => [p.id, p.name]));
  const nameOf = (id) => names[id] || (id || "").slice(0, 8);
  const reqPending = selected?.request_status === "pending";

  return (
    <div className="space-y-3" data-testid="admin-chats-panel">
      <ChatStats stats={stats} onRefresh={loadStats} loading={statsLoading} />
      <ChatSettings initial={stats?.settings} onSaved={(chat) => setStats((s) => (s ? { ...s, settings: chat } : s))} />

      <div className="yalola-card overflow-hidden grid grid-cols-1 md:grid-cols-[320px_1fr] min-w-0">
        <div className="border-b md:border-b-0 md:border-r flex flex-col max-h-[45vh] md:max-h-[70vh] min-w-0" style={{ borderColor: "var(--yalola-border)" }}>
          <div className="p-3 space-y-2 border-b" style={{ borderColor: "var(--yalola-border)" }}>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--yalola-text-muted)" }} />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un membre…" className="w-full rounded-full border pl-8 pr-3 py-1.5 text-sm outline-none" style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-surface-alt)" }} data-testid="chat-audit-search" />
            </div>
            <div className="flex gap-1.5">
              {[["all", "Toutes"], ["requests", "Demandes en attente"]].map(([k, l]) => (
                <button key={k} onClick={() => setFilter(k)} className={`pill text-xs ${filter === k ? "pill-primary" : "pill-outline"}`} data-testid={`chat-audit-filter-${k}`}>{l}</button>
              ))}
            </div>
            <div className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>⚠️ Chaque ouverture est journalisée (audit logs).</div>
          </div>
          <div className="overflow-y-auto flex-1">
            {convos.length === 0 && <div className="p-4 text-xs" style={{ color: "var(--yalola-text-muted)" }} data-testid="chat-audit-empty">Aucune conversation.</div>}
            {convoPager.slice.map((c) => (
              <button key={c.id} onClick={() => open(c)} className="w-full text-left p-3 hover:bg-[color:var(--yalola-surface-alt)] border-b" style={{ background: selected?.id === c.id ? "var(--yalola-surface-alt)" : "transparent", borderColor: "var(--yalola-border)" }} data-testid={`admin-conv-${c.id}`}>
                <div className="text-sm font-medium flex items-center gap-1.5 flex-wrap">
                  {(c.participant_details || []).map((p) => p.name).join(" ↔ ")}
                  {c.request_status === "pending" && <Badge bg="#FEF3C7" color="#B45309" testId={`admin-conv-request-${c.id}`}>Demande en attente</Badge>}
                </div>
                <div className="text-xs truncate" style={{ color: "var(--yalola-text-muted)" }}>{c.last_message || "—"}</div>
              </button>
            ))}
            <PagerBar page={convoPager.page} pages={convoPager.pages} total={convoPager.total} shown={convoPager.slice.length} onPage={convoPager.setPage} noun="conversation" testId="chats-pager" className="px-3 pb-3" />
          </div>
        </div>

        <div className="p-4 max-h-[70vh] overflow-y-auto space-y-2 min-w-0">
          {!selected && <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }}>Sélectionne une conversation.</p>}
          {selected && reqPending && (
            <div className="rounded-xl p-2 text-xs" style={{ background: "#FEF3C7", color: "#92400E" }} data-testid="chat-audit-request-banner">
              Demande de message en attente chez <b>{nameOf(selected.request_for)}</b> — l'expéditeur n'est pas suivi par le destinataire ; aucune notification tant qu'elle n'est pas acceptée.
            </div>
          )}
          {selected && selected.request_status === "accepted" && selected.accepted_at && (
            <div className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }} data-testid="chat-audit-accepted">Demande acceptée le {fmtDT(selected.accepted_at)}</div>
          )}
          {loadingMsgs && <div className="py-6 text-center"><Loader2 size={18} className="animate-spin mx-auto" /></div>}
          {!loadingMsgs && selected && msgs.length === 0 && <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }}>Aucun message.</p>}
          {!loadingMsgs && selected && (
            <PurgeConversation conv={selected} count={msgs.length} onPurged={(id) => { setConvos((cs) => cs.filter((c) => c.id !== id)); setSelected(null); setMsgs([]); loadStats(); }} />
          )}
          {!loadingMsgs && msgs.map((m) => {
            const starCount = (m.starred_by || []).length;
            const delFor = (m.deleted_for || []).map(nameOf);
            return (
              <div key={m.id} className="border rounded-xl p-2 min-w-0" style={{ borderColor: m.is_deleted ? "#e11d48" : "var(--yalola-border)" }} data-testid={`audit-msg-${m.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-semibold" data-testid={`audit-msg-sender-${m.id}`}>{nameOf(m.sender_id)}</span>
                  {m.is_deleted && <Badge bg="#FEE2E2" color="#B91C1C" testId={`audit-msg-deleted-${m.id}`}>🚫 Supprimé</Badge>}
                  {m.edited && <Badge bg="#FEF3C7" color="#B45309" testId={`audit-msg-edited-${m.id}`}>✏️ Modifié</Badge>}
                  {Array.isArray(m.masked_words) && m.masked_words.length > 0 && <Badge bg="#E0E7FF" color="#3730A3" testId={`audit-msg-masked-${m.id}`}>🔇 Mot masqué</Badge>}
                  {starCount > 0 && <Badge bg="#FEF9C3" color="#854D0E" testId={`audit-msg-starred-${m.id}`}><Star size={9} className="inline -mt-0.5" fill="#854D0E" /> {starCount}</Badge>}
                  {delFor.length > 0 && <Badge bg="#F3F4F6" color="#4B5563" testId={`audit-msg-deleted-for-${m.id}`}>Supprimé pour moi par {delFor.join(", ")}</Badge>}
                  {m.forwarded && <Badge bg="#F3F4F6" color="#4B5563">↪ Transféré</Badge>}
                </div>
                {m.media_url && <div className="my-1 max-w-[240px]"><ChatMedia msg={m} /></div>}
                {m.content && <div className="text-sm break-words" style={{ color: m.is_deleted ? "var(--yalola-text-muted)" : undefined }}>{m.is_deleted ? <i>« {m.content} »</i> : m.content}</div>}
                {m.original_content != null && m.original_content !== m.content && (
                  <div className="text-[11px] mt-1 p-1.5 rounded bg-amber-50 border border-amber-200" data-testid={`audit-msg-original-${m.id}`}>
                    <span className="font-semibold text-amber-800">{m.masked_words?.length && !m.edited ? "Texte original (avant masquage) :" : "Original :"}</span> <span className="text-amber-900 break-words">{m.original_content}</span>
                  </div>
                )}
                <div className="text-[10px] flex items-center gap-2 flex-wrap" style={{ color: "var(--yalola-text-muted)" }}>
                  <span>{fmtDT(m.created_at)}{m.deleted_at ? ` · supprimé le ${fmtDT(m.deleted_at)}${m.deleted_by ? ` par ${nameOf(m.deleted_by)}` : ""}` : ""}{m.read_at ? ` · vu ${fmtDT(m.read_at)}` : ""}</span>
                  <PurgeMessage msg={m} onPurged={(id) => { setMsgs((ms) => ms.filter((x) => x.id !== id)); loadStats(); }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ChatAuditPanel;
